import { useEffect, useState, type FormEvent } from "react";

import { Button } from "../../components/Button/Button";
import { Dialog } from "../../components/Dialog/Dialog";
import { FormField, Select, TextInput, Toggle } from "../../components/FormField/FormField";
import { ApiError, type UserPayload, type UserRow, api } from "../../lib/api";
import { getRole, rolePayload, type UserRoleValue } from "../../lib/users";

interface UserFormDialogProps {
  open: boolean;
  user: UserRow | null;
  onClose: () => void;
  onSaved: (user: UserRow) => void;
}

interface FormState {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: UserRoleValue;
  is_active: boolean;
  password: string;
}

const EMPTY: FormState = {
  username: "",
  email: "",
  first_name: "",
  last_name: "",
  role: "user",
  is_active: true,
  password: "",
};

export function UserFormDialog({ open, user, onClose, onSaved }: UserFormDialogProps) {
  const isEdit = user !== null;
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (user) {
      setForm({
        username: user.username,
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        role: getRole(user),
        is_active: user.is_active,
        password: "",
      });
    } else {
      setForm(EMPTY);
    }
    setError(null);
  }, [open, user]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (!form.username.trim()) {
      setError("O nome de utilizador é obrigatório.");
      return;
    }
    if (!isEdit && form.password.length < 8) {
      setError("A palavra-passe tem de ter pelo menos 8 caracteres.");
      return;
    }

    const payload: UserPayload = {
      username: form.username.trim(),
      email: form.email.trim(),
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      is_active: form.is_active,
      ...rolePayload(form.role),
    };
    if (!isEdit && form.password) {
      payload.password = form.password;
    }

    setSubmitting(true);
    try {
      const result = isEdit
        ? await api.updateUser(user!.id, payload)
        : await api.createUser(payload);
      onSaved(result);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(extractFieldErrors(err.body) ?? err.message);
      } else {
        setError("Não foi possível guardar o utilizador.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEdit ? "Editar utilizador" : "Criar utilizador"}
      size="md"
      footer={
        <>
          <Button variant="ghost" size="md" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button variant="primary" size="md" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "A guardar…" : isEdit ? "Guardar alterações" : "Criar"}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <FormField label="Nome de utilizador" htmlFor="username">
          <TextInput
            id="username"
            value={form.username}
            onChange={(e) => update("username", e.target.value)}
            disabled={submitting || isEdit}
            autoComplete="off"
          />
        </FormField>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <FormField label="Primeiro nome" htmlFor="first_name">
            <TextInput
              id="first_name"
              value={form.first_name}
              onChange={(e) => update("first_name", e.target.value)}
              disabled={submitting}
            />
          </FormField>
          <FormField label="Apelido" htmlFor="last_name">
            <TextInput
              id="last_name"
              value={form.last_name}
              onChange={(e) => update("last_name", e.target.value)}
              disabled={submitting}
            />
          </FormField>
        </div>

        <FormField label="Email" htmlFor="email">
          <TextInput
            id="email"
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            disabled={submitting}
          />
        </FormField>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignItems: "end" }}>
          <FormField label="Função" htmlFor="role">
            <Select
              id="role"
              value={form.role}
              onChange={(e) => update("role", e.target.value as UserRoleValue)}
              disabled={submitting}
            >
              <option value="user">Utilizador</option>
              <option value="staff">Operador</option>
              <option value="admin">Administrador</option>
            </Select>
          </FormField>
          <Toggle
            label="Conta activa"
            checked={form.is_active}
            onChange={(e) => update("is_active", e.target.checked)}
            disabled={submitting}
          />
        </div>

        {!isEdit && (
          <FormField
            label="Palavra-passe"
            htmlFor="password"
            hint="Mínimo de 8 caracteres."
          >
            <TextInput
              id="password"
              type="password"
              value={form.password}
              onChange={(e) => update("password", e.target.value)}
              disabled={submitting}
              autoComplete="new-password"
            />
          </FormField>
        )}

        {error && <div style={errorStyle}>{error}</div>}
      </form>
    </Dialog>
  );
}

const errorStyle: React.CSSProperties = {
  padding: "8px 12px",
  background: "#ffe5e5",
  color: "#b00020",
  borderRadius: 12,
  fontWeight: 600,
  fontSize: 13,
};

function extractFieldErrors(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const lines: string[] = [];
  for (const [field, value] of Object.entries(body)) {
    if (Array.isArray(value)) {
      lines.push(`${field}: ${value.join(" ")}`);
    } else if (typeof value === "string") {
      lines.push(value);
    }
  }
  return lines.length ? lines.join(" • ") : null;
}
